import type CardClient from '@atlaskit/link-provider/client';
import { isSuccessfulResponse } from '@atlaskit/link-provider/responses';
import type { BatchResponse } from '@atlaskit/link-provider/responses';
import { extractLink } from '@atlaskit/link-extractors';
import type { JsonLd } from '@atlaskit/json-ld-types/jsonld';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { jsonLdToEnhancedSmartLink } from './jsonLdToEnhancedSmartLink.ts';
import type { EnhancedSmartLink } from './smartLinkTypes.ts';

// `fetchDataAris` issues a single request for the whole array (no internal
// chunking), and the ORS batch endpoint caps a request at 50 ARIs.
const MAX_ARIS_PER_BATCH = 50;

const chunk = <T>(items: readonly T[], size: number): T[][] => {
	const chunks: T[][] = [];
	for (let i = 0; i < items.length; i += size) {
		chunks.push(items.slice(i, i + size));
	}
	return chunks;
};

/**
 * Hydrates a list of entity ARIs into `EnhancedSmartLink`s via the Object
 * Resolver Service (`cardClient.fetchDataAris` → `/resolve/ari/batch`), reusing
 * the same generic JSON-LD mapping as the outgoing-links path. This is the
 * ARI analogue of `collectSmartLinks`.
 *
 * ARIs are resolved in batches of {@link MAX_ARIS_PER_BATCH}. A failed batch is
 * reported to monitoring and skipped so one bad batch does not sink the rest.
 * Unresolvable ARIs (`ErrorResponse`) are dropped; access-restricted entities
 * still resolve to a link carrying their `access` status.
 */
export async function resolveIncomingLinks(
	aris: string[],
	cardClient: CardClient,
): Promise<EnhancedSmartLink[]> {
	if (aris.length === 0) {
		return [];
	}

	const batchResults: (BatchResponse | null)[] = await Promise.all(
		chunk(aris, MAX_ARIS_PER_BATCH).map((batch) =>
			cardClient.fetchDataAris(batch).catch((err: unknown) => {
				const cause = err instanceof Error ? err : new Error(String(err));
				getMonitoringClient().submitError(
					new Error(`Failed to resolve incoming link ARIs: ${batch.join(', ')}`, { cause }),
					{ attribution: Attribution.INTEGRATIONS },
				);
				return null;
			}),
		),
	);

	// A null entry is a failed batch (already reported above); drop it, then
	// flatten the per-batch arrays into one list, keep the successful responses,
	// and map each to a link. These chained iterators are linear, not quadratic:
	// work is bounded to ≤1000 ARIs total (resolved in batches of 50), so the perf
	// rule's heuristic is a false positive here. `occurrenceIndex` is the position
	// among resolved links, preserving the query's recency ordering.
	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
	return batchResults
		.filter((batchResult): batchResult is BatchResponse => batchResult !== null)
		.flat()
		.filter(isSuccessfulResponse)
		.map((item, index) => {
			const response = item.body;
			const url = extractLink(response.data as JsonLd.Data.BaseData) ?? '';
			// The ORS batch resolver can return shallow Jira payloads, so opt into normalization.
			return jsonLdToEnhancedSmartLink(url, index, response, true);
		});
}
